/* eslint-disable react/forbid-prop-types */
import PropTypes from 'prop-types';
import { isHTMLElement } from './dom';

const htmlElementPropType = (props, propName, componentName) => {
  const value = props[propName];
  const isElement = isHTMLElement(value);
  if (!isElement) {
    return new TypeError(`Invalid HTML Element Prop Value: ${propName} in ${componentName}`);
  }
  return null;
};

export const portalNodeType = PropTypes.shape({
  element: htmlElementPropType,
  elementType: PropTypes.string,
  getInitialPortalProps: PropTypes.func,
  mount: PropTypes.func,
  setPortalProps: PropTypes.func,
  unmount: PropTypes.func,
});

export const pricePerCurrencyType = PropTypes.shape({
  currency: PropTypes.string.isRequired,
  formattedPrice: PropTypes.string.isRequired,
  price: PropTypes.number.isRequired,
});

const flightClassType = PropTypes.shape({
  key: PropTypes.string.isRequired,
  pricePerCurrency: PropTypes.arrayOf(pricePerCurrencyType).isRequired,
  option: PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    flightClass: PropTypes.string.isRequired,
    fullDescription: PropTypes.string.isRequired,
    itineraryDescription: PropTypes.string.isRequired,
  }).isRequired,
  soldOut: PropTypes.bool.isRequired,
  limitedAvailability: PropTypes.bool,
  limitedAvailabilityRemainingSpaces: PropTypes.string,
  limitedAvailabilityText: PropTypes.string,
});

const flightOptionType = PropTypes.shape({
  key: PropTypes.string.isRequired,
  option: PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    fullDescription: PropTypes.string.isRequired,
    itineraryDescription: PropTypes.string.isRequired,
    outboundFlightNumber: PropTypes.string,
    outboundDepartureAirport: PropTypes.string,
    outboundArrivalAirport: PropTypes.string,
    outboundDepartureDate: PropTypes.string,
    outboundDepartureTime: PropTypes.string,
    inboundFlightNumber: PropTypes.string,
    inboundDepartureAirport: PropTypes.string,
    inboundArrivalAirport: PropTypes.string,
    inboundDepartureDate: PropTypes.string,
    inboundDepartureTime: PropTypes.string,
  }).isRequired,
  flightClasses: PropTypes.arrayOf(flightClassType).isRequired,
  soldOut: PropTypes.bool,
  limitedAvailability: PropTypes.bool,
});

const accommodationOptionType = PropTypes.shape({
  accommodationType: PropTypes.string.isRequired,
  key: PropTypes.string.isRequired,
  pricePerCurrency: PropTypes.arrayOf(pricePerCurrencyType).isRequired,
  option: PropTypes.shape({
    accommodationType: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    fullDescription: PropTypes.string.isRequired,
    itineraryDescription: PropTypes.string.isRequired,
  }).isRequired,
  roomTypes: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      roomName: PropTypes.string.isRequired,
      code: PropTypes.string.isRequired,
    })
  ).isRequired,
});

const ticketOptionType = PropTypes.shape({
  key: PropTypes.string.isRequired,
  day: PropTypes.number.isRequired,
  defaultOption: PropTypes.shape({
    key: PropTypes.string.isRequired,
    pricePerCurrency: PropTypes.arrayOf(pricePerCurrencyType).isRequired,
    option: PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      category: PropTypes.string.isRequired,
      fullDescription: PropTypes.string.isRequired,
      itineraryDescription: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  upgrades: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      pricePerCurrency: PropTypes.arrayOf(pricePerCurrencyType).isRequired,
      option: PropTypes.shape({
        key: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        category: PropTypes.string.isRequired,
        fullDescription: PropTypes.string.isRequired,
        itineraryDescription: PropTypes.string.isRequired,
      }).isRequired,
    })
  ),
});

const imageType = PropTypes.shape({
  url: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  focalPointLeft: PropTypes.number.isRequired,
  focalPointTop: PropTypes.number.isRequired,
});

export const tourType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  key: PropTypes.string.isRequired,
  tourId: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  listingTitle: PropTypes.string.isRequired,
  headerTitle: PropTypes.string.isRequired,
  displayPricePerCurrency: PropTypes.arrayOf(pricePerCurrencyType).isRequired,
  fromDate: PropTypes.string.isRequired,
  toDate: PropTypes.string.isRequired,
  locations: PropTypes.arrayOf(PropTypes.string).isRequired,
  matches: PropTypes.arrayOf(PropTypes.string),
  tournament: PropTypes.string.isRequired,
  ticketOptions: PropTypes.arrayOf(ticketOptionType).isRequired,
  flightOptions: PropTypes.arrayOf(flightOptionType),
  accommodationOptions: PropTypes.arrayOf(accommodationOptionType),
  transportOptions: PropTypes.arrayOf(PropTypes.shape({})),
  excursionOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      fullDescription: PropTypes.string.isRequired,
      image: imageType.isRequired,
      pricePerCurrency: PropTypes.arrayOf(pricePerCurrencyType).isRequired,
    })
  ),
  extraOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      fullDescription: PropTypes.string.isRequired,
      itineraryDescription: PropTypes.string.isRequired,
      image: imageType.isRequired,
      pricePerCurrency: PropTypes.arrayOf(pricePerCurrencyType).isRequired,
    })
  ),
  hasStopovers: PropTypes.bool.isRequired,
  hasMerchandise: PropTypes.bool.isRequired,
});
